import { useState, useEffect, useCallback } from 'react';
import logger from '@/utils/logger';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  Languages, ArrowLeft, RefreshCw, Loader2, Search, Save, RotateCcw,
  ChevronRight, Edit3, Check, X, FolderOpen, Globe2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Section card component
const SectionCard = ({ section, onClick, isSelected }) => (
  <button
    onClick={onClick}
    className={`w-full text-left p-4 rounded-xl border transition-all ${
      isSelected 
        ? 'border-primary bg-primary/5 shadow-sm' 
        : 'border-stone-200 hover:border-primary/50 hover:bg-stone-50'
    }`}
  >
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <FolderOpen className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-stone-400'}`} />
        <div>
          <p className="font-medium capitalize">{section.name}</p>
          <p className="text-sm text-muted-foreground">{section.key_count} tekstiä</p>
        </div>
      </div>
      <ChevronRight className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-stone-300'}`} />
    </div>
  </button>
);

// Editable translation row
const TranslationRow = ({ translationKey, value, onChange, onSave, isModified }) => {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  const handleSave = () => {
    onChange(translationKey, editValue);
    onSave();
    setEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setEditing(false);
  };

  // Get readable label from key
  const getLabel = (key) => {
    const parts = key.split('.');
    return parts[parts.length - 1].replace(/([A-Z])/g, ' $1').trim();
  };

  return (
    <div className={`p-4 rounded-xl border transition-all ${
      isModified ? 'border-amber-300 bg-amber-50' : 'border-stone-200 hover:border-stone-300'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <p className="text-xs text-muted-foreground font-mono mb-1 truncate" title={translationKey}>
            {translationKey}
          </p>
          {editing ? (
            <div className="flex items-center gap-2">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                className="flex-1"
                autoFocus
              />
              <Button size="sm" variant="ghost" onClick={handleSave}>
                <Check className="h-4 w-4 text-green-600" />
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel}>
                <X className="h-4 w-4 text-red-600" />
              </Button>
            </div>
          ) : (
            <p className="text-sm">{value || <span className="text-stone-400 italic">Tyhjä</span>}</p>
          )}
        </div>
        {!editing && (
          <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
            <Edit3 className="h-4 w-4" />
          </Button>
        )}
      </div>
      {isModified && (
        <p className="text-xs text-amber-600 mt-2">Muokattu - muista tallentaa</p>
      )}
    </div>
  );
};

export default function AdminTranslations() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  
  const [selectedLanguage, setSelectedLanguage] = useState('fi');
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [sectionTranslations, setSectionTranslations] = useState({});
  const [modifiedKeys, setModifiedKeys] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  // Load sections
  const loadSections = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/translations/sections`, {
        params: { language: selectedLanguage },
        withCredentials: true
      });
      setSections(response.data.sections);
      setIsAdmin(true);
    } catch (error) {
      if (error.response?.status === 403) {
        setIsAdmin(false);
      } else {
        logger.error('Failed to load sections:', error);
        toast.error('Osioiden lataus epäonnistui');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedLanguage]);

  // Load section translations
  const loadSectionTranslations = useCallback(async (sectionName) => {
    try {
      const response = await axios.get(`${API}/admin/translations/section/${sectionName}`, {
        params: { language: selectedLanguage },
        withCredentials: true
      });
      setSectionTranslations(response.data.translations);
      setModifiedKeys(new Set());
    } catch (error) {
      logger.error('Failed to load translations:', error);
      toast.error('Käännösten lataus epäonnistui');
    }
  }, [selectedLanguage]);

  useEffect(() => {
    loadSections();
  }, [loadSections]);

  useEffect(() => {
    if (selectedSection) {
      loadSectionTranslations(selectedSection);
    }
  }, [selectedSection, loadSectionTranslations]);

  // Handle translation change
  const handleTranslationChange = (key, value) => {
    setSectionTranslations(prev => ({
      ...prev,
      [key]: value
    }));
    setModifiedKeys(prev => new Set([...prev, key]));
  };

  // Save modified translations
  const handleSave = async () => {
    if (modifiedKeys.size === 0) {
      toast.info('Ei muutoksia tallennettavaksi');
      return;
    }

    setSaving(true);
    try {
      const translations = Array.from(modifiedKeys).map(key => ({
        key,
        value: sectionTranslations[key]
      }));

      await axios.put(`${API}/admin/translations`, {
        language: selectedLanguage,
        translations
      }, { withCredentials: true });

      toast.success(`${modifiedKeys.size} tekstiä tallennettu`);
      setModifiedKeys(new Set());
    } catch (error) {
      logger.error('Failed to save translations:', error);
      toast.error('Tallennus epäonnistui');
    } finally {
      setSaving(false);
    }
  };

  // Reset translations
  const handleReset = async () => {
    if (!window.confirm('Haluatko varmasti palauttaa kaikki käännökset alkuperäisiin? Tätä ei voi perua.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/translations/reset`, {
        params: { language: selectedLanguage },
        withCredentials: true
      });
      toast.success('Käännökset palautettu alkuperäisiin');
      loadSections();
      if (selectedSection) {
        loadSectionTranslations(selectedSection);
      }
    } catch (error) {
      logger.error('Failed to reset translations:', error);
      toast.error('Palautus epäonnistui');
    }
  };

  // Filter translations by search
  const filteredTranslations = Object.entries(sectionTranslations).filter(([key, value]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return key.toLowerCase().includes(query) || 
           (value && value.toLowerCase().includes(query));
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16 text-center">
          <Languages className="h-16 w-16 text-stone-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Pääsy estetty</h1>
          <p className="text-muted-foreground mb-6">
            Vain ylläpitäjät voivat muokata käännöksiä.
          </p>
          <Button onClick={() => navigate('/')}>
            Takaisin etusivulle
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/admin/analytics')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Takaisin
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Languages className="h-6 w-6 text-primary" />
                Tekstien Hallinta
              </h1>
              <p className="text-muted-foreground">
                Muokkaa sivuston tekstejä suomeksi ja englanniksi
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Language selector */}
            <div className="flex rounded-lg border border-stone-200 overflow-hidden">
              <button
                onClick={() => setSelectedLanguage('fi')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  selectedLanguage === 'fi' 
                    ? 'bg-primary text-white' 
                    : 'bg-white hover:bg-stone-50'
                }`}
              >
                <span className="text-lg">🇫🇮</span> Suomi
              </button>
              <button
                onClick={() => setSelectedLanguage('en')}
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 transition-colors ${
                  selectedLanguage === 'en' 
                    ? 'bg-primary text-white' 
                    : 'bg-white hover:bg-stone-50'
                }`}
              >
                <span className="text-lg">🇬🇧</span> English
              </button>
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleReset}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Palauta
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sections sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-stone-200 p-4">
              <h2 className="font-semibold mb-4 flex items-center gap-2">
                <Globe2 className="h-4 w-4" />
                Osiot
              </h2>
              <div className="space-y-2">
                {sections.map(section => (
                  <SectionCard
                    key={section.name}
                    section={section}
                    isSelected={selectedSection === section.name}
                    onClick={() => setSelectedSection(section.name)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Translations editor */}
          <div className="lg:col-span-3">
            {selectedSection ? (
              <div className="bg-white rounded-2xl border border-stone-200 p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                  <div>
                    <h2 className="text-lg font-semibold capitalize">{selectedSection}</h2>
                    <p className="text-sm text-muted-foreground">
                      {filteredTranslations.length} tekstiä
                      {modifiedKeys.size > 0 && (
                        <span className="text-amber-600 ml-2">
                          ({modifiedKeys.size} muokattu)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Hae tekstejä..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                      />
                    </div>
                    <Button
                      onClick={handleSave}
                      disabled={modifiedKeys.size === 0 || saving}
                      className="gap-2"
                    >
                      {saving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      Tallenna
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {filteredTranslations.map(([key, value]) => (
                    <TranslationRow
                      key={key}
                      translationKey={key}
                      value={value}
                      onChange={handleTranslationChange}
                      onSave={() => {}}
                      isModified={modifiedKeys.has(key)}
                    />
                  ))}
                  
                  {filteredTranslations.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Search className="h-12 w-12 mx-auto mb-4 text-stone-300" />
                      <p>Ei hakutuloksia</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                <FolderOpen className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold mb-2">Valitse osio</h2>
                <p className="text-muted-foreground">
                  Valitse vasemmalta osio, jonka tekstejä haluat muokata
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-2xl p-6">
          <h3 className="font-semibold text-blue-900 mb-2">Ohje</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Valitse ensin kieli (Suomi tai English) ylhäältä</li>
            <li>• Valitse osio vasemmalta (esim. &quot;landing&quot; = etusivu)</li>
            <li>• Klikkaa kynäikonia muokataksesi tekstiä</li>
            <li>• Muista tallentaa muutokset &quot;Tallenna&quot; -napista</li>
            <li>• &quot;Palauta&quot; palauttaa kaikki tekstit alkuperäisiin</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
